import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Avatar,
    Button,
    Checkbox,
    Dropdown,
    Icon,
    ScrollableTable,
    ScrollableTableBody,
    ScrollableTableCell,
    ScrollableTableHead,
    ScrollableTableRow,
    ScrollableTableSortableHeaderCells,
    Stack,
    styled,
    Text,
} from 'shell/components';
import { useDraggableScroll } from 'shell/hooks';
import { FilterOption } from 'utils/constants';

const GradingStatistics = ({
    statisticsFilters: filters,
    setStatisticsFilters: setFilters,
    onGraderFilterChange,
    exportStatistics,
    filterOptions,
    tableData,
    visibleColumns,
    onColumnToggle,
    isTableExpanded,
    onToggleTableExpanded,
    sortState,
    handleSortState,
    columns,
    device,
    isLmsAssignment = false,
}) => {
    const tableRef = useRef(null);
    const [expandedHeight, setExpandedHeight] = useState(0);
    const { t } = useTranslation('assignment');
    const { ref: criterionRef, events: criterionEvents, isDragging: criterionIsDragging } = useDraggableScroll();
    const { ref: graderRef, events: graderEvents, isDragging: graderIsDragging } = useDraggableScroll();

    const columnWidth = `${100 / (visibleColumns.length + 1)}%`;

    const visibleHeaderColumns = columns
        ?.filter((col) => col.key === 'name' || visibleColumns.includes(col.key))
        .map((col) => ({
            ...col,
            width: columnWidth,
        }));

    useLayoutEffect(() => {
        if (tableRef.current) {
            setExpandedHeight(tableRef.current.scrollHeight);
        }
    }, [tableData, visibleColumns]);

    return (
        <BackgroundStack spacing={5}>
            <Stack spacing={3}>
                <Stack
                    direction={device === 'mobile' ? 'column' : 'row'}
                    justifyContent='space-between'
                    alignItems={device === 'mobile' ? 'flex-start' : 'center'}
                    spacing={5}
                >
                    <Text variant='p2' weight='medium'>
                        {t('submission.grading-statistics-student-level', 'Grading Statistics: Student Level')}
                    </Text>
                    <Button
                        startIcon={<Icon kind='download' />}
                        variant='secondary'
                        onClick={exportStatistics}
                        fullWidth={device === 'mobile'}
                    >
                        {t('common:controls.export-to-csv', 'Export to CSV')}
                    </Button>
                </Stack>

                <Stack spacing={3}>
                    <HorizontalScrollStack
                        direction='row'
                        ref={criterionRef}
                        {...criterionEvents}
                        isDragging={criterionIsDragging}
                    >
                        {filterOptions.criteria.map((opt) => (
                            <FilterChip
                                key={opt.id}
                                variant='secondary'
                                color='neutral'
                                onClick={() =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        criterion:
                                            prev.criterion === opt.id && opt.id !== FilterOption.ALL
                                                ? FilterOption.ALL
                                                : opt.id,
                                    }))
                                }
                                isSelected={filters.criterion === opt.id}
                                tooltip={{ component: opt.title }}
                            >
                                {opt.title}
                            </FilterChip>
                        ))}
                    </HorizontalScrollStack>
                </Stack>
            </Stack>

            <Stack>
                <Text variant='p2'>View by grader</Text>
                <Stack
                    direction={device === 'mobile' ? 'column' : 'row'}
                    justifyContent='space-between'
                    alignItems={device === 'mobile' ? 'flex-start' : 'center'}
                    gap={5}
                >
                    <HorizontalScrollStack
                        direction='row'
                        ref={graderRef}
                        {...graderEvents}
                        isDragging={graderIsDragging}
                    >
                        {filterOptions.graders.map((opt) => (
                            <FilterChip
                                key={opt.id}
                                variant='secondary'
                                color='neutral'
                                onClick={() => onGraderFilterChange(opt.id)}
                                isSelected={
                                    Array.isArray(filters.grader)
                                        ? filters.grader.includes(opt.id)
                                        : filters.grader === opt.id
                                }
                                tooltip={{ component: opt.name }}
                            >
                                {opt.name}
                            </FilterChip>
                        ))}
                    </HorizontalScrollStack>
                    <Dropdown
                        id={`grading-statistics-columns-dropdown`}
                        stayOpen
                        sx={{ width: device === 'mobile' ? '100%' : 'auto' }}
                        options={columns
                            .filter((col) => col.key !== 'name')
                            .map((col) => {
                                const isLastVisible = visibleColumns.length === 1 && visibleColumns.includes(col.key);
                                return {
                                    key: col.key,
                                    disabled: isLastVisible,
                                    value: (
                                        <Checkbox
                                            checked={visibleColumns.includes(col.key)}
                                            disabled={isLastVisible}
                                            label={col.columnTitle}
                                            onChange={() => onColumnToggle(col.key)}
                                        />
                                    ),
                                };
                            })}
                        rootComponent={
                            <Button
                                variant='secondary'
                                color='neutral'
                                startIcon={<Icon kind='column' />}
                                fullWidth={device === 'mobile'}
                            >
                                {t('common:ui.columns', 'Columns')}
                            </Button>
                        }
                    />
                </Stack>
            </Stack>

            <Stack spacing={2}>
                <CollapsibleTable
                    ref={tableRef}
                    enableDrag
                    isExpanded={isTableExpanded}
                    expandedHeight={expandedHeight}
                    aria-label='grading-statistics-table'
                >
                    <ScrollableTableHead>
                        <ScrollableTableRow>
                            <ScrollableTableSortableHeaderCells
                                columns={visibleHeaderColumns}
                                sortState={sortState}
                                handleSetSortState={(newState) => handleSortState(newState.orderBy)}
                            />
                        </ScrollableTableRow>
                    </ScrollableTableHead>
                    <ScrollableTableBody>
                        {tableData.map((row) => (
                            <ScrollableTableRow key={row.id}>
                                <ScrollableTableCell width={columnWidth}>
                                    <Stack direction='row' alignItems='center' spacing={2}>
                                        {isLmsAssignment && <Avatar user={row.avatar} size='small' />}
                                        <Text variant='p2'>{row.name}</Text>
                                    </Stack>
                                </ScrollableTableCell>
                                {columns.map(
                                    (col) =>
                                        visibleColumns.includes(col.key) && (
                                            <ScrollableTableCell key={col.key} width={columnWidth}>
                                                {row[col.rowKey]}
                                            </ScrollableTableCell>
                                        )
                                )}
                            </ScrollableTableRow>
                        ))}
                        {tableData.length === 0 && (
                            <ScrollableTableRow>
                                <ScrollableTableCell colSpan={1 + visibleColumns.length} align='center'>
                                    <EmptyStateText variant='p2'>
                                        {t(
                                            'submission.no-filtered-graded-submissions',
                                            'No graded submissions found for the current filters.'
                                        )}
                                    </EmptyStateText>
                                </ScrollableTableCell>
                            </ScrollableTableRow>
                        )}
                    </ScrollableTableBody>
                </CollapsibleTable>
                {expandedHeight > 345 && (
                    <TableToggleButton
                        variant='borderless'
                        color='primary'
                        onClick={() => onToggleTableExpanded(!isTableExpanded)}
                    >
                        {isTableExpanded
                            ? t('common:controls.show-less', 'Show Less')
                            : t('common:controls.show-more', 'Show More')}
                    </TableToggleButton>
                )}
            </Stack>
        </BackgroundStack>
    );
};

const HorizontalScrollStack = styled(Stack)(({ isDragging }) => ({
    overflowX: 'auto',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
    '-ms-overflow-style': 'none',
    'scrollbar-width': 'none',
    cursor: isDragging ? 'grabbing' : 'grab',
    userSelect: 'none',
    width: '100%',
    paddingLeft: '0.25rem',

    '& > button:first-child': {
        marginLeft: '-0.25rem',
    },
}));

const EmptyStateText = styled(Text)(({ theme }) => ({
    paddingBlock: theme.spacing(16),
}));

const FilterChip = styled(Button)(({ theme, isSelected }) => {
    return {
        fontWeight: 'normal',
        maxWidth: '180px',
        padding: '0.625rem',
        margin: '0.5rem 0.2rem',

        '& > div.MuiBox-root': {
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
        },

        ...(isSelected && {
            borderColor: theme.palette.ensightful.primary[500],
            backgroundColor: theme.palette.ensightful.primary[50],
            color: theme.palette.ensightful.primary[700],

            '&:hover': {
                backgroundColor: theme.palette.ensightful.primary[100],
            },
        }),
    };
});

const TableToggleButton = styled(Button)(() => {
    return {
        width: 'fit-content',
    };
});

const CollapsibleTable = styled(ScrollableTable, {
    shouldForwardProp: (prop) => prop !== 'isExpanded',
})(({ isExpanded }) => {
    return {
        borderRadius: '12px',
        overflow: 'hidden',
        maxHeight: isExpanded ? '100%' : '345px',
        transition: 'max-height 0.3s ease-out',

        '& .MuiTableCell-root': {
            minWidth: '176px',
            '& p': {
                whiteSpace: 'normal',
            },
        },

        '& div.MuiBox-root': {
            marginBottom: 0,
        },
    };
});

const BackgroundStack = styled(Stack)(({ theme }) => {
    return {
        backgroundColor: theme.palette.ensightful.shades[0],
        padding: '1.25rem',
        borderRadius: '12px',
    };
});

export default GradingStatistics;
